import { useCallback, useMemo } from "react";
import { useBlastCampaign } from "../../context";
import {
  buildEmailPayload,
  buildEmailBindings,
  buildTemplateVariables,
  extractContactFields,
  buildEventSourceList,
} from "../../utils/emailPayloadBuilder";
import { validateEmailPayload } from "../../utils/fieldValidation";
import { CONTACT_SOURCES } from "../../constants/blastConstants";

/**
 * Hook for managing email template configuration
 */
export const useEmailTemplate = () => {
  const {
    contactSource,
    blastContacts,
    csvParsedContacts,
    fieldMappings,
    emailPayload,
    emailBindings,
    setEmailPayload,
    setEmailBindings,
    updateEmailField,
  } = useBlastCampaign();

  /**
   * Get available contact fields based on source
   */
  const availableFields = useMemo(() => {
    if (contactSource === CONTACT_SOURCES.CSV) {
      // For CSV, use the mapped field names
      return Object.entries(fieldMappings)
        .filter(([, value]) => value)
        .map(([key]) => key);
    } else {
      // For database, extract fields from contacts
      return extractContactFields(blastContacts);
    }
  }, [contactSource, fieldMappings, blastContacts]);

  /**
   * Get template variables for the editor
   */
  const templateVariables = useMemo(() => {
    return buildTemplateVariables(availableFields, contactSource);
  }, [availableFields, contactSource]);

  /**
   * Get event source list for template editor
   */
  const eventSourceList = useMemo(() => {
    return buildEventSourceList(availableFields);
  }, [availableFields]);

  /**
   * Get current email field values as an object
   */
  const emailForm = useMemo(() => {
    const form = {};
    emailPayload.forEach((item) => {
      form[item.field] = item.value;
    });
    return form;
  }, [emailPayload]);

  /**
   * Validate current email configuration
   */
  const validation = useMemo(() => {
    return validateEmailPayload(emailPayload);
  }, [emailPayload]);

  /**
   * Update email form field
   */
  const handleFieldChange = useCallback(
    (field, value) => {
      updateEmailField(field, value);
    },
    [updateEmailField]
  );

  /**
   * Update payload from template editor export
   */
  const handleTemplateExport = useCallback(
    ({ html, design, variables, form }) => {
      // Build payload from form
      const newPayload = buildEmailPayload({
        ...form,
        body: html || form.body,
      });

      // Build bindings from variables
      const newBindings = buildEmailBindings(variables, availableFields);

      setEmailPayload(newPayload);
      setEmailBindings(newBindings);

      return { payload: newPayload, bindings: newBindings };
    },
    [availableFields, setEmailPayload, setEmailBindings]
  );

  /**
   * Save current payload configuration
   */
  const savePayload = useCallback(() => {
    const newBindings = buildEmailBindings(
      templateVariables.filter((v) => v.origin === "EVENT"),
      availableFields
    );
    setEmailBindings(newBindings);

    return {
      payload: emailPayload,
      bindings: newBindings,
    };
  }, [templateVariables, availableFields, emailPayload, setEmailBindings]);

  /**
   * Get email subject (convenience getter)
   */
  const subject = useMemo(() => {
    return emailPayload.find((p) => p.field === "subject")?.value || "";
  }, [emailPayload]);

  /**
   * Get email body (convenience getter)
   */
  const body = useMemo(() => {
    return emailPayload.find((p) => p.field === "body")?.value || "";
  }, [emailPayload]);

  /**
   * Check if template is ready (has required fields)
   */
  const isTemplateReady = useMemo(() => {
    return validation.isValid;
  }, [validation.isValid]);

  return {
    // State
    emailPayload,
    emailBindings,
    emailForm,

    // Computed
    availableFields,
    templateVariables,
    eventSourceList,
    validation,
    subject,
    body,
    isTemplateReady,

    // Actions
    handleFieldChange,
    handleTemplateExport,
    savePayload,
    setEmailPayload,
    setEmailBindings,
  };
};

export default useEmailTemplate;
